import React, { useState, useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Axios from 'axios';
import {
  EditorState,
  ContentState,
  convertFromHTML,
  convertToRaw,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Row, Col, Card } from 'react-bootstrap';
import {
  Alert,
  DataStatus,
  ActionButton,
  BackButton,
  Input,
  TextEditor,
  DatePicker,
} from 'components';
import { RupiahConvert, DateConvert, TableNumber } from 'utilities';
import { PenawaranApi } from 'api';
import { TablePenawaran, TableLC, TableMP } from '../TablePenawaran';
import PeluangPenawaran from '../PeluangPenawaran';

const TambahPenawaran = ({ setNavbarTitle }) => {
  const { id_rab } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [dataRAB, setDataRAB] = useState({});
  const [dataBarangJadi, setDataBarangJadi] = useState([]);
  const [dataListLC, setDataListLC] = useState([]);
  const [dataListMP, setDataListMP] = useState([]);
  const [noPenawaran, setNoPenawaran] = useState('');
  const [totalPenawaran, setTotalPenawaran] = useState(0);
  const [totalLC, setTotalLC] = useState(0);
  const [totalMP, setTotalMP] = useState(0);
  const [dataPrelim, setDataPrelim] = useState([]);
  const [modalPeluangConfig, setModalPeluangConfig] = useState({
    show: false,
    no_peluang: '',
  });

  // STATE TEXT EDITOR
  const [textEditorState, setTextEditorState] = useState(() => {
    return EditorState.createWithContent(
      ContentState.createFromBlockArray(
        convertFromHTML(
          '<p>Dengan hormat,<br />Bersama ini kami sampaikan penawaran harga sebagai berikut :</p>'
        )
      )
    );
  });
  const [textEditorStatePenutup, setTextEditorStatePenutup] = useState(() => {
    return EditorState.createWithContent(
      ContentState.createFromBlockArray(
        convertFromHTML(
          `<p>Catatan :<br />
          <b> - Tidak termasuk : hardware, finishing, kaca, pemasangan on site.
          <br />  -  Temasuk : </b>
          <br />  -  Harga berdasarkan ukuran dan spesifikasi pada gambar. 
          <br />  -  Pembayaran uang muka 30% dan sisanya di bayar setelah barang selesai.
          <br />  -  Harga berlaku 6 bulan setelah penawaran ini diterima.
          <br />Demikian kami sampaikan dan terimakasih.</p>`
        )
      )
    );
  });

  const [alertConfig, setAlertConfig] = useState({
    show: false,
    variant: 'primary',
    text: '',
  });

  const getInitialData = () => {
    setIsPageLoading(true);

    //GET SINGLE DETAIL RAE
    Axios.all([PenawaranApi.getSingleRab({ id_rab: id_rab })])
      .then(
        Axios.spread((data) => {
          const dataInfo = data?.data?.data ?? {};
          const filterPrelim = dataInfo?.detail_prelim?.map((val, index) => ({
            id_kelompok: val.id_kelompok,
            id_item: val.id_item_prelim,
            kode_item: val.kode_item_prelim,
            nama_item: val.nama_item_prelim,
            qty_item: val.qty_analisa,
            id_unit: val.id_satuan_prelim,
            nama_unit: val.nama_satuan_prelim,
            harga: parseFloat(val.unit_price ?? 0),
            qty_durasi: parseFloat(val.qty_durasi ?? 0),
            konstanta: parseFloat(val.konstanta ?? 0),
            order: index + 1,
          }));

          setDataRAB(dataInfo);
          setDataBarangJadi(dataInfo.detail_barang_jadi ?? []);
          setDataPrelim(filterPrelim ?? []);
        })
      )
      .catch(() => {
        // setIsFetchingFailed(true)
        setAlertConfig({
          show: true,
          variant: 'danger',
          text: 'Data gagal dimuat!',
        });
      })
      .finally(() => setIsPageLoading(false));
  };

  const updateEditorState = (value, setFieldValue, editor) => {
    editor === 'teks_pembuka'
      ? setTextEditorState(value)
      : setTextEditorStatePenutup(value);

    const convertData = convertToRaw(value.getCurrentContent());

    setFieldValue(editor, draftToHtml(convertData));
  };

  // GET NOMOR RAB
  const getNomorPenawaran = (tanggal) => {
    PenawaranApi.getNomorPenawaran({ tanggal: tanggal })
      .then((no_penawaran) => {
        setNoPenawaran(no_penawaran?.data?.data);
      })
      .catch((err) => alert(err.response.data.data))
      .finally(() => setIsPageLoading(false));
  };

  const checkAlert = () => {
    const locationState = location.state;

    if (locationState) {
      if (locationState.alert) {
        setAlertConfig({
          show: locationState.alert.show,
          text: locationState.alert.text,
          variant: locationState.alert.variant,
        });
      }
    }
  };

  const InfoSection = () => {
    const InfoItem = ({ title, value, link, onClick }) => (
      <tr onClick={onClick}>
        <td>{title}</td>
        <td className={`pl-4 pr-2`}>:</td>
        <td
          className={link ? 'text-primary' : ''}
          style={{ cursor: link ? 'pointer' : 'default' }}
        >
          {value}
        </td>
      </tr>
    );

    return (
      <Row>
        <Col>
          <table style={{ fontSize: '14px' }}>
            <tbody>
              <InfoItem
                title="Tgl. RAB"
                value={
                  dataRAB?.tgl_rab
                    ? DateConvert(new Date(dataRAB.tgl_rab)).detail
                    : '-'
                }
              />
              <InfoItem title="No. RAB" value={dataRAB?.no_rab ?? '-'} />
              <InfoItem
                link
                title="No. Peluang"
                value={dataRAB?.no_peluang ?? '-'}
                onClick={() =>
                  setModalPeluangConfig({
                    show: true,
                    no_peluang: dataRAB?.no_peluang ?? '',
                  })
                }
              />
              <InfoItem title="Proyek" value={dataRAB?.nama_proyek ?? '-'} />
            </tbody>
          </table>
        </Col>
        <Col>
          <table style={{ fontSize: '14px' }}>
            <tbody>
              <InfoItem
                title="Customer"
                value={dataRAB?.nama_customer ?? '-'}
              />
              <InfoItem title="ATT" value={dataRAB?.att ?? '-'} />
              <InfoItem
                title="Tgl. Selesai"
                value={
                  dataRAB?.tgl_selesai
                    ? DateConvert(new Date(dataRAB.tgl_selesai)).detail
                    : '-'
                }
              />
            </tbody>
          </table>
        </Col>
      </Row>
    );
  };

  const getGrandTotalPrelim = dataPrelim.reduce((prev, current) => {
    const newQtyItem = current.qty_item ? parseFloat(current.qty_item) : 0;
    const newHarga = current.harga ? parseFloat(current.harga) : 0;
    const newQtyDurasi = current.qty_durasi
      ? parseFloat(current.qty_durasi)
      : 0;
    const newKonts = current.konstanta ? parseFloat(current.konstanta) : 0;
    const subTotal = newQtyItem * newHarga;
    const total = subTotal * newQtyDurasi * newKonts;

    return prev + total;
  }, 0);

  const GrandTotalSection = ({ totalRAB, totalLC, totalMP }) => {
    const hitungGrandTotal = () => {
      let grandTotal = totalRAB + totalLC + totalMP;
      return RupiahConvert(String(grandTotal)).detail;
    };

    useEffect(() => {
      hitungGrandTotal();
    }, [totalRAB, totalLC, totalMP]);

    return (
      <div className="p-2 bg-white text-right border">
        <b className="text-nowrap pr-3" style={{ fontSize: '14px' }}>
          Grand Total : {hitungGrandTotal()}
        </b>
      </div>
    );
  };

  useEffect(() => {
    setNavbarTitle('Penawaran');
    getInitialData();
    checkAlert();
    getNomorPenawaran(new Date().toISOString().slice(0, 10));

    return () => {
      setIsPageLoading(false);
    };
  }, []);

  // INITIAL VALUES FORM TAMBAH DATA TIPE BARANG JADI
  const formInitialValues = {
    id_rab: id_rab,
    tgl_penawaran: new Date().toISOString().slice(0, 10),
    no_penawaran: noPenawaran,
    ppn: 0,
    diskon: 0,
    teks_pembuka:
      '<p>Dengan hormat,<br />Bersama ini kami sampaikan penawaran harga sebagai berikut :</p>',
    teks_penutup: `<p>Catatan :<br />
          <b> - Tidak termasuk : hardware, finishing, kaca, pemasangan on site.
          <br />  -  Temasuk : </b>
          <br />  -  Harga berdasarkan ukuran dan spesifikasi pada gambar. 
          <br />  -  Pembayaran uang muka 30% dan sisanya di bayar setelah barang selesai.
          <br />  -  Harga berlaku 6 bulan setelah penawaran ini diterima.
          <br />Demikian kami sampaikan dan terimakasih.</p>`,
  };

  // FORMIK VALIDATION SCHEMA TIPE BARANG JADI DENGAN YUP VALIDATION
  const formValidationSchema = Yup.object().shape({
    tgl_penawaran: Yup.date().required('Tanggal Penawaran wajib diisi'),
    ppn: Yup.number().required('PPN wajib diisi'),
    diskon: Yup.number().required('Diskon wajib diisi'),
    teks_pembuka: Yup.string(),
    teks_penutup: Yup.string(),
  });

  // KIRIM UPDATE DATA KE SERVER
  const formSubmitHandler = (values) => {
    PenawaranApi.save(values)
      .then((res) => {
        history.push('/transaksi/penawaran', {
          alert: {
            show: true,
            variant: 'primary',
            text: 'Tambah data berhasil!',
          },
        });
      })
      .catch((err) => {
        // ERROR ALERT
        setAlertConfig({
          show: true,
          variant: 'danger',
          text: `Tambah data gagal! (${err.response.data.message})`,
        });
      });
  };

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <b>Tambah Penawaran</b>
          <div className="d-flex align-items-center">
            {!isPageLoading && (
              <ActionButton
                variant="success"
                text="Ubah RAB"
                className="mb-1 mr-2"
                onClick={() =>
                  history.push({
                    pathname:
                      '/transaksi/rencana-anggaran-biaya/ubah/' +
                      dataRAB?.id_rab,
                    state: { nonBaseline: true },
                  })
                }
              />
            )}
            <BackButton onClick={() => history.goBack()} />
          </div>
        </Card.Header>
        <Card.Body>
          <Alert
            showCloseButton
            show={alertConfig.show}
            variant={alertConfig.variant}
            text={alertConfig.text}
            onClose={() =>
              setAlertConfig({
                ...alertConfig,
                show: false,
              })
            }
          />
          {isPageLoading ? (
            <DataStatus loading={true} text="Memuat data . . ." />
          ) : (
            <>
              <InfoSection />
              <hr />
            </>
          )}

          <Formik
            initialValues={formInitialValues}
            validationSchema={formValidationSchema}
            onSubmit={formSubmitHandler}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              handleChange,
              handleSubmit,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                {!isPageLoading && (
                  <div className="row mt-4">
                    <div className="col-md-6">
                      <DatePicker
                        portalId="root-portal"
                        label="Tgl. Realisasi Penawaran"
                        name="tgl_penawaran"
                        selected={
                          values.tgl_penawaran
                            ? new Date(values.tgl_penawaran)
                            : ''
                        }
                        onChange={(val) => {
                          const value = val.toISOString().slice(0, 10);
                          setFieldValue('tgl_penawaran', value);
                          getNomorPenawaran(value);
                        }}
                        error={
                          errors.tgl_penawaran && touched.tgl_penawaran && true
                        }
                        errorText={errors.tgl_penawaran}
                      />
                    </div>

                    <div className="col-md-6">
                      <Input
                        label="No. Penawaran"
                        type="text"
                        name="no_penawaran"
                        value={noPenawaran}
                        readOnly={true}
                      />
                    </div>
                  </div>
                )}

                {!isPageLoading && (
                  <div className="row mt-2">
                    <div className="col">
                      <TextEditor
                        label="Teks Pembuka"
                        placeholder="Tuliskan teks pembuka"
                        editorState={textEditorState}
                        onEditorStateChange={(val) => {
                          updateEditorState(val, setFieldValue, 'teks_pembuka');
                        }}
                        error={
                          errors.teks_pembuka && touched.teks_pembuka && true
                        }
                        errorText={
                          errors.teks_pembuka &&
                          touched.teks_pembuka &&
                          errors.teks_pembuka
                        }
                      />
                    </div>
                  </div>
                )}

                {!isPageLoading && (
                  <>
                    <TablePenawaran
                      ppn={values.ppn}
                      diskon={values.diskon}
                      setFieldValue={setFieldValue}
                      setTotalPenawaran={setTotalPenawaran}
                      totalPenawaran={totalPenawaran}
                      dataBarangJadi={dataBarangJadi}
                      setDataBarangJadi={setDataBarangJadi}
                      dataPrelim={dataPrelim}
                    />
                    <GrandTotalSection
                      totalRAB={totalPenawaran}
                      totalLC={totalLC}
                      totalMP={totalMP}
                    />
                    <div className="row mt-2">
                      <div className="col">
                        <TextEditor
                          label="Teks Penutup"
                          placeholder="Tuliskan teks penutup"
                          editorState={textEditorStatePenutup}
                          onEditorStateChange={(val) => {
                            updateEditorState(
                              val,
                              setFieldValue,
                              'teks_penutup'
                            );
                          }}
                          error={
                            errors.teks_penutup && touched.teks_penutup && true
                          }
                          errorText={
                            errors.teks_penutup &&
                            touched.teks_penutup &&
                            errors.teks_penutup
                          }
                        />
                      </div>
                    </div>
                    <div className="text-right mt-3">
                      <ActionButton
                        type="submit"
                        className="btn btn-primary text-white"
                        size="md"
                        text="Simpan Penawaran"
                        loading={isSubmitting}
                      />
                    </div>
                  </>
                )}
              </form>
            )}
          </Formik>
        </Card.Body>
      </Card>

      {/* Modal Peluang */}
      {modalPeluangConfig.show && (
        <PeluangPenawaran
          type="TAMBAH"
          modalPeluangConfig={modalPeluangConfig}
          setModalPeluangConfig={setModalPeluangConfig}
        />
      )}
    </>
  );
};

export default TambahPenawaran;
